import { asyncFlatMap, asyncForEach, layoutToWorkspace, orderedLayouts, safeInteropRegister, toGoLang, toGoLangInvoke, workspaceToLayout } from "../../api-polyfill/src/common/windowFunctions";
export class WorkspaceService {
    constructor(glue, logger, router) {
        this.glue = glue;
        this.logger = logger;
        this.router = router;
        this.eventHandlers = {};
    }
    async triggerEventHandlers({ event, workspaceName }, applicationName) {
        const data = [];
        if (!this.eventHandlers[event]) {
            return { data };
        }
        for (const uuid of this.eventHandlers[event]) {
            this.logger.debug(`workspaces - WorkspaceClient.triggerEventHandlers ${event} ${workspaceName}`, `From ${applicationName}`, uuid);
            const { data: layout } = await toGoLang(() => this.glue.layouts.get(workspaceName, "Global"));
            const workspaceImport = layout ? layoutToWorkspace(layout) : { name: workspaceName, version: "1.0.0", type: "workspace", groups: {}, windows: [] };
            const { err, data: result, invocationResult } = await toGoLangInvoke(() => this.glue.interop.invoke(uuid, workspaceImport));
            if (err || !result) {
                this.logger.error("workspaces - WorkspaceClient.triggerEventHandlers error", err?.toString());
                return { err };
            }
            this.logger.debug(`workspaces - WorkspaceClient.triggerEventHandlers ${event} ${workspaceName}`, `Handled by ${invocationResult?.executed_by?.applicationName}`, result);
            const maybeModifiedData = result?.newData ? workspaceToLayout(workspaceName, result.newData, layout?.metadata?.["iop#position"] ?? 0) : null;
            if (maybeModifiedData) {
                this.logger.debug(`workspaces - WorkspaceClient.triggerEventHandlers ${event} ${workspaceName} modified layout=`, maybeModifiedData);
            }
            data.push({
                action: result?.action,
                newData: maybeModifiedData
            });
        }
        return {
            data
        };
    }
    async workspaceEventRelay() {
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.addEventListener", safeInteropRegister(({ event, uuid }, caller) => {
            this.logger.debug(`workspaces - WorkspaceClient.addEventListener ${event}`, caller.applicationName, uuid);
            if (!this.eventHandlers[event]) {
                this.eventHandlers[event] = [];
            }
            this.eventHandlers[event].push(uuid);
        }, this.logger));
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.removeEventListener", safeInteropRegister(({ event, uuid }, caller) => {
            this.logger.debug(`workspaces - WorkspaceClient.removeEventListener ${this.eventHandlers[event]}`, caller.applicationName, event);
            if (this.eventHandlers[event]?.length > 0) {
                const index = this.eventHandlers[event].indexOf(uuid);
                if (index >= 0) {
                    this.eventHandlers[event].splice(index, 1);
                }
            }
            if (this.eventHandlers[event].length === 0) {
                delete this.eventHandlers[event];
            }
        }, this.logger));
        await this.glue.interop.register("FSBL.Clients.WorkspaceClient.triggerEventHandlers", safeInteropRegister(async (data, caller) => {
            return await this.triggerEventHandlers(data, caller.applicationName);
        }, this.logger));
    }
    async broadcastUpdate(reason) {
        const currentLayout = await this.glue.layouts.getCurrentLayout();
        if (!currentLayout) {
            return;
        }
        const layouts = await this.glue.layouts.getAll("Global");
        const ordered = await orderedLayouts(layouts);
        const activeWorkspace = layoutToWorkspace(currentLayout);
        const workspaces = ordered.map((layout) => layout.name);
        const workspacesConfig = await asyncFlatMap(ordered, async (layoutSummary) => {
            const layout = await this.glue.layouts.get(layoutSummary.name, "Global");
            return layout ? [layoutToWorkspace(layout)] : [];
        });
        const state = {
            activeWorkspace,
            workspaces,
            workspacesConfig,
            reason
        };
        this.logger.debug("workspaces - Layouts updated (see console for full state)");
        console.debug("Layouts updated", state);
        this.router.publish("Finsemble.WorkspaceService.update", state);
    }
    async loadInitialWorkspace(name, manifest) {
        const { data: allLayouts } = await toGoLang(() => this.glue.layouts.getAll("Global"));
        this.logger.debug("workspaces - workspaces - Trying to load initial layout", name);
        if (allLayouts?.some((layout) => layout.name === name)) {
            await toGoLang(() => this.glue.layouts.get(name, "Global"));
        }
        else if (allLayouts?.length) {
            this.logger.debug(`workspaces - Initial layout "${name}" not found. Selecting first available layout: "${allLayouts[0].name}".`);
            name = allLayouts[0].name;
        }
        else {
            const emptyWorkspace = {
                "version": "1.0.0",
                "name": "Empty Workspace",
                "type": "workspace",
                "groups": {},
                "windows": []
            };
            this.logger.debug(`workspaces - Initial layout "${name}" not found. Creating "Empty Workspace".`);
            name = emptyWorkspace.name;
            const layout = workspaceToLayout(emptyWorkspace.name, emptyWorkspace, 0);
            const { err } = await toGoLang(() => this.glue.layouts.import([layout], "merge"));
            if (err) {
                this.logger.error("workspaces - Error creating \"Empty Workspace\" layout", layout, err);
            }
        }
        const { data } = await this.triggerEventHandlers({ event: "load-requested", workspaceName: name }, "finsemble-service") ?? { data: null };
        if (data) {
            const layouts = [];
            data.forEach((datum) => {
                layouts.push(datum.newData);
            });
            await toGoLang(() => this.glue.layouts.import(layouts, "merge"));
        }
        this.logger.debug(`workspaces - Restoring initial layout ${name}`);
        const { err: err2 } = await toGoLang(() => this.glue.layouts.restore({ name }));
        await this.triggerEventHandlers({ event: "load-complete", workspaceName: name }, "finsemble-service");
        if (err2) {
            this.logger.error(`workspaces - Error restoring initial layout ${name}`, err2);
        }
        else {
            this.logger.debug("workspaces - Restored initial layout", name);
        }
        await toGoLang(() => this.glue.prefs.setFor("IO.Global.FinsembleLastWorkspace", { name }));
    }
    async maybeLoadInitialWorkspace(manifest) {
        const prefs = await this.glue.prefs.get("IO.Global.FinsemblePreferences");
        const lastWorkspacePref = await this.glue.prefs.get("IO.Global.FinsembleLastWorkspace");
        const initialWorkspace = prefs?.data?.["finsemble.initialWorkspace"] ?? manifest?.finsemble?.initialWorkspace ?? lastWorkspacePref?.data?.name ?? manifest?.finsemble?.workspaces?.[0]?.name;
        this.logger.debug(`workspaces - initialWorkspace "${initialWorkspace}" is being loaded`);
        await this.loadInitialWorkspace(initialWorkspace, manifest);
    }
    async maybeSetUpDefaultWorkspaces(manifest) {
        const workspaces = manifest.finsemble?.workspaces ?? [];
        const { data: allLayouts = [] } = await toGoLang(() => this.glue.layouts.getAll("Global"));
        await asyncForEach(workspaces, async (workspace) => {
            if (!allLayouts.find((layout) => layout.name === workspace.name)) {
                this.logger.debug(`workspaces - Adding layout ${workspace.name} from workspaces.json`);
                const layout = workspaceToLayout(workspace.name, workspace, 0);
                const { err } = await toGoLang(() => this.glue.layouts.import([layout], "merge"));
                if (err) {
                    this.logger.error(`workspaces - Error creating ${workspace.name} layout`, layout, err);
                }
            }
        });
    }
    async loadWorkspacesPostSSO(manifest) {
        await toGoLang(() => this.glue.layouts.clearDefaultGlobal());
        await this.maybeSetUpDefaultWorkspaces(manifest);
        await this.maybeLoadInitialWorkspace(manifest);
        this.broadcastUpdate("Finsemble.Workspace.GetActiveWorkspace");
    }
    async start() {
        await this.workspaceEventRelay();
        await new Promise(sleep => setTimeout(sleep, 5));
        this.glue.layouts.onAdded(() => { this.broadcastUpdate("Finsemble.Workspace.NewWorkspace"); });
        this.glue.layouts.onChanged(() => { this.broadcastUpdate("Finsemble.Workspace.Save"); });
        this.glue.layouts.onRemoved(() => { this.broadcastUpdate("Finsemble.Workspace.Remove"); });
        this.glue.layouts.onRenamed(() => { this.broadcastUpdate("Finsemble.Workspace.Rename"); });
        this.glue.layouts.onRestored(() => { this.broadcastUpdate("Finsemble.Workspace.SwitchTo"); });
    }
}
